import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPeriod } from "@/lib/indicator-period";
import { cn } from "@/lib/utils";
import type { IndicatorDirection, IndicatorPeriod, IndicatorValueRow } from "@/lib/types";

/**
 * Indicator trend chart — values + target line per period, drawn as a
 * single inline SVG to avoid pulling in a chart library. Colour-coded by
 * the indicator's direction: increasing → emerald (down = bad), decreasing
 * → rose (up = bad), stable → slate.
 *
 * Empty / single-value states get tidy placeholders so the card never
 * shows a broken axis.
 */
interface Props {
  values: IndicatorValueRow[];
  period: IndicatorPeriod;
  direction: IndicatorDirection;
  unit: string;
  loading?: boolean;
}

const W = 720, H = 220, PAD_X = 36, PAD_TOP = 12, PAD_BOTTOM = 24;
const CHART_W = W - PAD_X * 2;
const CHART_H = H - PAD_TOP - PAD_BOTTOM;

export function IndicatorTrendChart({ values, period, direction, unit, loading }: Props) {
  if (loading) {
    return (
      <Card className="border-border/60 shadow-soft">
        <CardHeader className="border-b border-border/60 py-4">
          <CardTitle className="text-base">Trend</CardTitle>
        </CardHeader>
        <CardContent className="p-5">
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Sort oldest→newest so the line reads left-to-right.
  const sorted = [...values].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="border-b border-border/60 py-4">
        <CardTitle className="flex items-baseline gap-2 text-base">
          Trend
          {sorted.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">
              {sorted.length} observation{sorted.length === 1 ? "" : "s"}
              {unit && <> · {unit}</>}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5">
        {sorted.length === 0 ? (
          <div className="rounded-md border border-dashed bg-muted/20 p-10 text-center text-sm text-muted-foreground">
            No observations yet. Add a value to plot the trend.
          </div>
        ) : (
          <Chart values={sorted} period={period} direction={direction} unit={unit} />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function Chart({
  values, period, direction, unit,
}: {
  values: IndicatorValueRow[];
  period: IndicatorPeriod;
  direction: IndicatorDirection;
  unit: string;
}) {
  // Numeric range covers both values + targets so a tall target band
  // doesn't push the line off the top edge.
  const allNums: number[] = [];
  values.forEach((v) => {
    const n = Number(v.value);
    if (Number.isFinite(n)) allNums.push(n);
    if (v.target != null) {
      const t = Number(v.target);
      if (Number.isFinite(t)) allNums.push(t);
    }
  });
  const max = Math.max(...allNums, 0);
  const min = Math.min(...allNums, 0);
  const span = max - min || 1;
  const yFor = (n: number) => PAD_TOP + (1 - (n - min) / span) * CHART_H;
  const xFor = (i: number) => values.length === 1
    ? PAD_X + CHART_W / 2
    : PAD_X + (CHART_W * i) / (values.length - 1);

  const colour =
    direction === "decreasing" ? "text-rose-500    dark:text-rose-300"
  : direction === "stable"      ? "text-slate-500   dark:text-slate-300"
                                : "text-emerald-500 dark:text-emerald-300";

  // Build SVG paths.
  const linePath = values
    .map((v, i) => `${i === 0 ? "M" : "L"} ${xFor(i)} ${yFor(Number(v.value))}`)
    .join(" ");
  // Filled area: line then close back to baseline.
  const areaPath = values.length > 1
    ? `${linePath} L ${xFor(values.length - 1)} ${H - PAD_BOTTOM} L ${xFor(0)} ${H - PAD_BOTTOM} Z`
    : "";
  const targets = values.filter((v) => v.target != null);
  const targetPath = targets
    .map((v, i) => {
      const idx = values.indexOf(v);
      return `${i === 0 ? "M" : "L"} ${xFor(idx)} ${yFor(Number(v.target!))}`;
    })
    .join(" ");

  // Three y-axis tick lines for orientation. Round nicely to whole numbers
  // when the span is wide, decimals for narrow ranges.
  const decimals = span < 10 ? 1 : 0;
  const tickVals = [min, min + span / 2, max].map((n) => +n.toFixed(decimals));

  return (
    <div className="space-y-2">
      <svg viewBox={`0 0 ${W} ${H}`} className={cn("w-full", colour)} role="img" aria-label="Indicator trend">
        {/* Gridlines + y labels */}
        {tickVals.map((t, i) => {
          const y = yFor(t);
          return (
            <g key={i}>
              <line x1={PAD_X} x2={W - PAD_X} y1={y} y2={y}
                className="stroke-border/60" strokeDasharray="2 4" />
              <text x={PAD_X - 6} y={y + 3} textAnchor="end"
                className="fill-muted-foreground text-[10px] font-mono tabular-nums">
                {t}{unit && i === tickVals.length - 1 ? unit : ""}
              </text>
            </g>
          );
        })}

        {/* Filled area under value line */}
        {areaPath && (
          <path d={areaPath} className="fill-current opacity-15" />
        )}

        {/* Target line (dashed) */}
        {targetPath && (
          <path d={targetPath} fill="none"
            className="stroke-foreground/40" strokeWidth={1.5}
            strokeDasharray="4 3" />
        )}

        {/* Value line */}
        <path d={linePath} fill="none" strokeLinejoin="round" strokeLinecap="round"
          className="stroke-current" strokeWidth={2} />

        {/* Value dots — give each a native title for tooltip */}
        {values.map((v, i) => (
          <g key={v.id}>
            <circle cx={xFor(i)} cy={yFor(Number(v.value))} r={3.5}
              className="fill-current stroke-card" strokeWidth={1.5}>
              <title>{formatPeriod(v.date, period)} · {prettyNum(v.value)}{unit ? ` ${unit}` : ""}{v.target != null ? `\nTarget: ${prettyNum(v.target)}${unit ? ` ${unit}` : ""}` : ""}</title>
            </circle>
          </g>
        ))}

        {/* X-axis period labels — every Nth so they don't overlap */}
        {values.map((v, i) => {
          const labelEvery = Math.max(1, Math.ceil(values.length / 7));
          if (i % labelEvery !== 0 && i !== values.length - 1) return null;
          return (
            <text key={`x-${v.id}`}
              x={xFor(i)} y={H - 6} textAnchor="middle"
              className="fill-muted-foreground text-[10px]">
              {formatPeriod(v.date, period)}
            </text>
          );
        })}
      </svg>

      {/* Small legend */}
      <div className="flex items-center justify-end gap-4 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("h-0.5 w-4 rounded", colour, "bg-current")} />
          Actual
        </span>
        {targetPath && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4 rounded bg-foreground/40" style={{ background: "repeating-linear-gradient(90deg, currentColor 0 4px, transparent 4px 7px)" }} />
            Target
          </span>
        )}
      </div>
    </div>
  );
}

function prettyNum(s: string): string {
  if (!s.includes(".")) return s;
  return s.replace(/0+$/, "").replace(/\.$/, "");
}
